import React, { useRef } from 'react';
import ScenarioIcon from '../components/MainPage/ScenarioIcon';
import AIProtocol, { ProtocolScenario } from '../components/MainPage/AIProtocol';
import useScenarioRotation, { SCENARIO_ROTATION_DURATION } from '../components/MainPage/useScenarioRotation';
import '../components/MainPage/ServiceCards.css';
import '../components/MainPage/AIExtras.css';

const scenarios: ProtocolScenario[] = [
  {
    id: 'emails',
    name: 'Письма',
    prompt: 'Прочитай переписку и подготовь письмо с итоговыми договорённостями.',
    intro: 'Прочитал всю цепочку и собрал итоговые договорённости.',
    lines: [
      'В черновике указал согласованные условия, сроки и следующий шаг.',
      'Добавил ответы на вопросы из последнего письма клиента.',
    ],
    source: 'Переписка с клиентом · 8 писем',
    materials: { pending: 'Читаем переписку', complete: 'Переписка разобрана' },
    processing: { pending: 'Сверяем договорённости', complete: 'Итоги собраны' },
    result: { pending: 'Готовим черновик', complete: 'Готов к отправке' },
    completionStatus: 'Черновик готов к отправке',
  },
  {
    id: 'meetings',
    name: 'Встречи',
    prompt: 'Что решили на встрече и кто за что отвечает?',
    intro: 'Договорились о следующем:',
    lines: [
      'Анна — уточнить требования к проекту. Срок — среда.',
      'Илья — показать прототип в пятницу.',
      'Команда — согласовать состав первого релиза.',
    ],
    source: 'Запись проектной встречи · 12:40–18:20',
    materials: { pending: 'Читаем расшифровку', complete: 'Запись разобрана' },
    processing: { pending: 'Ищем договорённости', complete: 'Выделены решения' },
    result: { pending: 'Собираем итоги', complete: 'Итоги встречи готовы' },
  },
  {
    id: 'documents',
    name: 'Документы',
    prompt: 'Обнови презентацию для клиента: сократи слайд о компании, добавь кейс и новые цены.',
    intro: 'Обновил файл «Презентация для клиента.pptx»:',
    lines: [
      'Сократил слайд о компании до трёх тезисов.',
      'Добавил кейс и заменил цены по актуальному прайс-листу.',
      'Проверил оформление. Презентация готова к отправке.',
    ],
    source: 'Презентация для клиента.pptx · Прайс-лист',
    materials: { pending: 'Читаем файлы', complete: 'Файлы загружены' },
    processing: { pending: 'Сопоставляем правки', complete: 'Правки определены' },
    result: { pending: 'Обновляем презентацию', complete: 'Презентация готова' },
  },
  {
    id: 'knowledge',
    name: 'База знаний',
    prompt: 'Клиент просит изготовить мебель по своим размерам. Найди наши условия.',
    intro: 'Нашёл раздел «Индивидуальные заказы»:',
    lines: [
      'Для расчёта нужны размеры, выбранный материал и эскиз.',
      'Возможность изготовления проверяет технолог.',
      'Стоимость и срок подтверждаем после согласования конструкции.',
    ],
    source: 'База знаний · «Индивидуальные заказы»',
    materials: { pending: 'Ищем в базе знаний', complete: 'Найдены условия' },
    processing: { pending: 'Сверяем с запросом', complete: 'Найдено правило' },
    result: { pending: 'Формулируем ответ', complete: 'Ответ готов к отправке' },
  },
  {
    id: 'data',
    name: 'Данные и отчётность',
    prompt: 'Какие заявки требуют внимания?',
    intro: 'В первую очередь стоит разобрать:',
    lines: [
      'Просроченные заявки — уточнить причину задержки.',
      'Повторные обращения — проверить, решена ли проблема.',
      'Заявки без движения — назначить ответственного и срок.',
    ],
    source: 'Реестр обращений · текущий период',
    materials: { pending: 'Читаем реестр', complete: 'Реестр загружен' },
    processing: { pending: 'Сравниваем заявки', complete: 'Выделены приоритеты' },
    result: { pending: 'Готовим сводку', complete: 'Заявки разобраны' },
  },
  {
    id: 'custom',
    name: 'Не нашли свой процесс?',
    prompt: 'У нас другой процесс. С чего начать?',
    intro: 'Расскажите команде о трёх вещах:',
    lines: [
      'Как сейчас устроен процесс и кто в нём участвует.',
      'Какая часть работы отнимает больше всего времени.',
      'Что вы хотите изменить и какие данные уже есть.',
    ],
    source: null,
    materials: { pending: 'Читаем запрос', complete: 'Запрос принят' },
    processing: { pending: 'Определяем вопросы', complete: 'Вопросы подобраны' },
    result: { pending: 'Готовим вопросы', complete: 'Вопросы готовы' },
  },
];

const AIPage: React.FC = () => {
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    active, cycle, running, select, restart,
  } = useScenarioRotation(panelRef, scenarios.length);
  const scenario = scenarios[active];

  const onTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let next = index;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      next = (index + 1) % scenarios.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      next = (index + scenarios.length - 1) % scenarios.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = scenarios.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    select(next);
    document.getElementById(`scenario-tab-${scenarios[next].id}`)?.focus();
  };

  return (
    <section
      aria-labelledby="ai-title"
      className="ai-section section-space"
      id="AI"
    >
      <div className="landing-container">
        <div
          className="section-heading"
          data-flow-reveal
        >
          <p className="eyebrow">
            <span className="section-index">01 /</span>
            {' '}
            ВНЕДРЕНИЕ РЕШЕНИЙ НА БАЗЕ ИИ
          </p>
          <div className="heading-columns">
            <h2 id="ai-title">
              Ускоряем процессы.
              <br />
              <span className="muted-title">Сокращаем рутину.</span>
            </h2>
            <p>
              ИИ разбирает письма, готовит документы и находит ответы в данных компании.
              {' '}
              Сотрудники проверяют результат и принимают решения.
            </p>
          </div>
        </div>
        <div
          className="scenario-layout"
          data-flow-reveal
        >
          <div
            aria-label="Сценарии внедрения ИИ"
            aria-orientation="vertical"
            className="scenario-tabs"
            role="tablist"
          >
            {scenarios.map((item, index) => (
              <button
                key={item.id}
                aria-controls="scenario-panel"
                aria-selected={active === index}
                className={`scenario-tab ${active === index ? 'is-active' : ''}`}
                id={`scenario-tab-${item.id}`}
                onClick={() => select(index)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                role="tab"
                tabIndex={active === index ? 0 : -1}
                type="button"
              >
                <ScenarioIcon kind={item.id} />
                <span>{item.name}</span>
                {active === index && (
                  <span
                    key={cycle}
                    aria-hidden="true"
                    className="scenario-progress"
                    style={{
                      animationDuration: `${SCENARIO_ROTATION_DURATION}ms`,
                      animationPlayState: running ? 'running' : 'paused',
                    }}
                  />
                )}
              </button>
            ))}
          </div>
          <div
            ref={panelRef}
            aria-labelledby={`scenario-tab-${scenario.id}`}
            className="scenario-panel"
            id="scenario-panel"
            role="tabpanel"
            tabIndex={0}
          >
            <div className="scenario-demos">
              {scenarios.map((item, index) => (
                <AIProtocol
                  key={item.id}
                  active={active === index}
                  onReplay={restart}
                  scenario={item}
                />
              ))}
            </div>
          </div>
        </div>
        <div
          aria-labelledby="ai-offer-title"
          className="ai-offer"
          id="AIStart"
          data-flow-reveal
        >
          <div className="ai-offer-main">
            <p className="eyebrow offer-kicker">ПРОСТОЕ ВНЕДРЕНИЕ</p>
            <div className="offer-intro">
              <h3 id="ai-offer-title">
                Начнём с одного
                <br />
                рабочего процесса.
              </h3>
            </div>
            <div className="offer-description">
              <p>
                Автоматизируем один рабочий процесс на базе готовых ИИ-инструментов.
                {' '}
                Настроим решение, проверим на ваших данных и передадим в эксплуатацию.
              </p>
              <span>В облаке или на серверах компании — с учётом требований к данным.</span>
            </div>
            <div className="offer-price">
              <p>
                <span className="price-amount">100 000</span>
                <span className="price-currency">₽</span>
              </p>
              <span>за внедрение одного процесса</span>
            </div>
          </div>
          <section
            aria-label="Дополнительные услуги"
            className="ai-capabilities"
          >
            <div className="ai-capabilities-grid">
              <article className="ai-capability">
                <h4>MCP-интеграции</h4>
                <p>Разрабатываем MCP-интеграции для ваших API и внутренних систем. Объём работ и оценку в часах согласуем заранее.</p>
                <span className="ai-capability-meta">
                  <strong>2 500 ₽</strong>
                  {' '}
                  / час
                </span>
              </article>
              <article className="ai-capability">
                <h4>Подписка на сервисы</h4>
                <p>Предоставим учётную запись с оплаченной подпиской на нужный ИИ-инструмент. Срок доступа согласуем заранее.</p>
                <span className="ai-capability-meta">Подписка по запросу</span>
              </article>
              <article className="ai-capability">
                <h4>Практика с ИИ</h4>
                <p>На ваших примерах покажем, как ставить задачи ИИ, уточнять запросы и оценивать результат.</p>
                <span className="ai-capability-meta">Дополнительные услуги оплачиваются отдельно.</span>
              </article>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

export default AIPage;
